import { createApi } from "@reduxjs/toolkit/query/react"
import { authQuery } from "@store/services/authQuery"

type ErrorScenarioSourceFileResponse = {
  message: string
}

export interface ValidationNodeDto {
  "Scenario Mnem"?: string
  "Forecast Mnem"?: string
  "Historic Mnem"?: string
}

interface ScenarioValidationDto {
  graphKey: string
  start_date: string | null
  end_date: string | null
  yearly_rolling_start_month: number
  simulation_frequency: string
  nodes: { [key: string]: ValidationNodeDto }
}

interface ScenarioValidationResponseDto {
  message: string[]
}

interface GraphValidationDto {
  key: string
  start_date: string | null
  end_date: string | null
  scenarios: string[]
}

interface GraphValidationResponseDto {
  message: string
  response: {
    message_id: string
    sns_response_message_id: string
    s3_file_path: string
    s3_file_version: string
  }
}

export interface GraphValidationResultDto {
  status?: "PROCESSING" | "SUCCESS" | "ERROR" | "TIMEOUT"
  /** ERRORS */
  message?: string[]
  /** warnings */
  warnings?: string[]
  execution_time?: string
}

export const documentApi = createApi({
  reducerPath: "documentApi",
  baseQuery: authQuery,
  tagTypes: ["Document", "DocumentValidation"],
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    checkScenarioSource: builder.mutation<any, FormData>({
      query: (body) => ({
        url: "/document/check-scenario-source",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Document"],
    }),
    checkScenarioSourceGraphKey: builder.mutation<
      ScenarioValidationResponseDto,
      ScenarioValidationDto
    >({
      query: ({
        graphKey,
        start_date,
        end_date,
        yearly_rolling_start_month,
        simulation_frequency,
        nodes,
      }) => ({
        url: `/document/check-scenario-source/${encodeURIComponent(graphKey)}`,
        method: "POST",
        body: {
          start_date,
          end_date,
          yearly_rolling_start_month,
          simulation_frequency,
          nodes,
        },
      }),
      invalidatesTags: ["DocumentValidation"],
    }),
    uploadScenarioSource: builder.mutation<
      { path: string; scenarios: string[]; scenario_hash: string; message: string },
      FormData
    >({
      query: (body) => ({
        url: "/document/upload-scenario-source",
        method: "POST",
        body,
      }),
      // Note: We also invalidate tags in UpdateMetadata/index.tsx after the CRDT is updated
      // This ensures both the document API and graphs API caches are invalidated
      invalidatesTags: ["Document"],
    }),
    deleteScenarioSource: builder.mutation<ErrorScenarioSourceFileResponse | string, string>({
      query: (payload) => {
        return {
          url: `/editor/static-scenario-source?graph_id=${encodeURIComponent(payload)}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ["Document"],
    }),
    validateGraphLambda: builder.mutation<GraphValidationResponseDto, GraphValidationDto>({
      query: ({ key, start_date, end_date, scenarios }) => ({
        url: `/document/validate-graph-lambda?graph_key=${encodeURIComponent(
          key,
        )}&start_date=${start_date}&end_date=${end_date}&raise_error=0&show_warnings=1&check_linked_graphs=1&scenario=${scenarios.join(
          ",",
        )}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["DocumentValidation"],
    }),
    getValidateGraphLambda: builder.query<GraphValidationResultDto, string>({
      query: (messageId) => ({
        url: `/document/validate-graph-lambda/result/${messageId}?raise_error=0`,
      }),
      providesTags: ["DocumentValidation"],
    }),
  }),
})
export const {
  useCheckScenarioSourceMutation,
  useCheckScenarioSourceGraphKeyMutation,
  useUploadScenarioSourceMutation,
  useDeleteScenarioSourceMutation,
  useValidateGraphLambdaMutation,
  useLazyGetValidateGraphLambdaQuery,
  useGetValidateGraphLambdaQuery,
} = documentApi
